/**
 * Dependency-free formatting utilities for timestamps with relative time and timezone support.
 * Converts UTC ISO timestamps to the user's local timezone and formats as relative time.
 */

const DisplayLocale = "en-US";

/**
 * Formats an ISO timestamp as relative time ("2 minutes ago", "in 3 hours") with
 * the absolute time available as a secondary display (for tooltips).
 *
 * @param isoTimestamp - The UTC instant to format.
 * @param now - The current instant to compare against (defaults to the real current time).
 * @returns An object with relativeTime (e.g., "2 minutes ago") and absoluteTime for displays.
 */
export function formatRelativeTimestamp(
  isoTimestamp: string | null | undefined,
  now: Date = new Date()
): { relativeTime: string; absoluteTime: string } | null {
  if (!isoTimestamp) {
    return null;
  }

  const then = new Date(isoTimestamp);
  const diffMs = now.getTime() - then.getTime();
  const minutes = Math.round(diffMs / 60000);

  let relativeTime: string;

  if (minutes < 0) {
    // Future time
    const absMinutes = Math.abs(minutes);
    if (absMinutes < 60) {
      relativeTime = `in ${absMinutes} minute${absMinutes === 1 ? "" : "s"}`;
    } else {
      const hours = Math.round(absMinutes / 60);
      if (hours < 24) {
        relativeTime = `in ${hours} hour${hours === 1 ? "" : "s"}`;
      } else {
        const days = Math.round(hours / 24);
        relativeTime = `in ${days} day${days === 1 ? "" : "s"}`;
      }
    }
  } else if (minutes < 1) {
    // Just now
    relativeTime = "moments ago";
  } else if (minutes < 60) {
    // Recent past: minutes
    relativeTime = `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  } else {
    // Past time: hours or days
    const hours = Math.round(minutes / 60);
    if (hours < 24) {
      relativeTime = `${hours} hour${hours === 1 ? "" : "s"} ago`;
    } else {
      const days = Math.round(hours / 24);
      if (days < 30) {
        relativeTime = `${days} day${days === 1 ? "" : "s"} ago`;
      } else {
        relativeTime = formatDayMonthYear(then);
      }
    }
  }

  // Absolute time in user's local timezone and locale
  const formattedDate = formatDayMonthYear(then);
  const formattedTime = then.toLocaleTimeString(DisplayLocale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const absoluteTime = `${formattedDate}, ${formattedTime}`;

  return { relativeTime, absoluteTime };
}

/**
 * Formats a date as "15 Dec 2024" (day, short month, year) regardless of locale ordering.
 */
function formatDayMonthYear(date: Date): string {
  const day = date.toLocaleDateString(DisplayLocale, { day: "numeric" });
  const month = date.toLocaleDateString(DisplayLocale, { month: "short" });
  const year = date.toLocaleDateString(DisplayLocale, { year: "numeric" });
  return `${day} ${month} ${year}`;
}
